import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  canShowScenarioStudioTrainingButton,
  REGISTRATION_PROFILE_JSON_KEY,
} from '@buxus/shared'
import { useAbp } from '../platform/abp/react'
import { PROFILE_STAKEHOLDER_STORAGE_KEY } from '../platform/abp/config'
import { mdiBookOpen, mdiFileDocumentOutline, mdiPresentationPlay, mdiToolboxOutline } from '@mdi/js'
import { InlineSpinner } from '../components/InlineSpinner'
import { NavMdiIcon } from '../NavTabIcons'
import { pathForAppState } from '../navigation/appPaths'
import { SHOW_AR_TRAINING_BUTTON, TRAINING_INTRO_TEXT } from './trainingCopy'
import {
  normalizeTrainingFeature,
  TRAINING_FEATURE_SCENARIO,
  TRAINING_FEATURE_TOOLBOX_TIP,
} from './trainingDocument'
import {
  TRAINING_LMS_SCREEN,
  TRAINING_SCENARIOS_SCREEN,
  TRAINING_TOOLBOX_TIPS_SCREEN,
} from './trainingScreens'
import { useTrainingDocuments } from './useTrainingDocuments'

type TrainingPortalScreenProps = {
  primaryHex: string
}

export function TrainingPortalScreen({ primaryHex }: TrainingPortalScreenProps) {
  const { bootstrap, featureEnabled, can } = useAbp()
  const studioEntryAudience = useMemo(() => {
    let registrationProfileData: unknown | null = null
    let stakeholderType: string | null = null
    try {
      const raw = localStorage.getItem(REGISTRATION_PROFILE_JSON_KEY)
      if (raw?.trim()) registrationProfileData = JSON.parse(raw) as unknown
    } catch {
      /* ignore */
    }
    try {
      stakeholderType = localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    return canShowScenarioStudioTrainingButton({
      tenantId: bootstrap?.tenantId,
      tenantName: bootstrap?.tenantName,
      registrationProfileData,
      stakeholderType,
    })
  }, [bootstrap?.tenantId, bootstrap?.tenantName])
  // Button only: feature + View + operator / ET-EC-ER. Direct /scenarios is not gated here.
  const showScenarioStudio =
    featureEnabled('Buxus.Scenarios') &&
    can('Buxus.Scenarios.View') &&
    studioEntryAudience
  const { documents, loading } = useTrainingDocuments()
  const hasScenarios = documents.some(
    (d) => normalizeTrainingFeature(d) === TRAINING_FEATURE_SCENARIO,
  )
  const hasToolboxTips = documents.some(
    (d) => normalizeTrainingFeature(d) === TRAINING_FEATURE_TOOLBOX_TIP,
  )
  return (
    <section className="app-download-card">
      <h2 className="app-download-title">Training Portal</h2>
      <p className="app-training-intro">{TRAINING_INTRO_TEXT}</p>
      <div className="app-training-actions">
        <Link
          to={pathForAppState(null, [TRAINING_LMS_SCREEN])}
          className="app-training-cta app-training-cta-with-icon app-download-btn primary"
          style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
        >
          <NavMdiIcon path={mdiBookOpen} size={1.2} className="app-training-cta-icon" />
          <span>Pipeline Playbook</span>
        </Link>
        {showScenarioStudio ? (
          <Link
            to="/scenarios"
            className="app-training-cta app-training-cta-with-icon app-download-btn primary"
            style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
          >
            <NavMdiIcon path={mdiPresentationPlay} size={1.2} className="app-training-cta-icon" />
            <span>Scenario Studio</span>
          </Link>
        ) : null}
        {SHOW_AR_TRAINING_BUTTON ? (
          <button type="button" className="app-training-cta app-download-btn primary" disabled>
            AR Responder Scenarios
          </button>
        ) : null}
        {loading ? (
          <div className="training-hub-loading">
            <InlineSpinner label="Loading training documents" />
          </div>
        ) : null}
        {!loading && hasScenarios ? (
          <Link
            to={pathForAppState(null, [TRAINING_SCENARIOS_SCREEN])}
            className="app-training-cta app-training-cta-with-icon app-download-btn primary"
            style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
          >
            <NavMdiIcon path={mdiFileDocumentOutline} size={1.2} className="app-training-cta-icon" />
            <span>Scenarios</span>
          </Link>
        ) : null}
        {!loading && hasToolboxTips ? (
          <Link
            to={pathForAppState(null, [TRAINING_TOOLBOX_TIPS_SCREEN])}
            className="app-training-cta app-training-cta-with-icon app-download-btn primary"
            style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
          >
            <NavMdiIcon path={mdiToolboxOutline} size={1.2} className="app-training-cta-icon" />
            <span>Toolbox Tips</span>
          </Link>
        ) : null}
      </div>
    </section>
  )
}
